import { createContext, useContext, useMemo, useState } from 'react';

/**
 * Defer-site-naming handoff state.
 *
 * When the deferred activation hook succeeds, the gate calls `setHandoff(...)`
 * with the post-activation identifiers. Downstream consumers (chiefly
 * `CloudStatusProvider`) read this context to learn the post-handoff values
 * WITHOUT depending on a URL rewrite + re-render cycle. The URL is still
 * rewritten in parallel as a refresh-survival aid, but it is no longer the
 * primary communication channel.
 *
 * Refs: D1D6-128.
 */
export type DeferSiteNamingHandoff = {
	readonly onDemandUrl: string;
	readonly provisioningRequestId: string;
	readonly signupId: string;
	readonly orchestrationId?: string;
	readonly cloudId?: string;
};

export type DeferSiteNamingHandoffContextValue = {
	readonly handoff: DeferSiteNamingHandoff | null;
	readonly setHandoff: (next: DeferSiteNamingHandoff) => void;
};

const DEFAULT_CONTEXT_VALUE: DeferSiteNamingHandoffContextValue = {
	handoff: null,
	setHandoff: () => {
		// no-op default; only meaningful when the provider is mounted.
	},
};

const DeferSiteNamingHandoffContext =
	createContext<DeferSiteNamingHandoffContextValue>(DEFAULT_CONTEXT_VALUE);

export const useDeferSiteNamingHandoff = (): DeferSiteNamingHandoffContextValue =>
	useContext(DeferSiteNamingHandoffContext);

export const DeferSiteNamingHandoffProvider = ({
	children,
}: {
	readonly children: React.ReactNode;
}) => {
	const [handoff, setHandoff] = useState<DeferSiteNamingHandoff | null>(null);
	const value = useMemo<DeferSiteNamingHandoffContextValue>(
		() => ({ handoff, setHandoff }),
		[handoff],
	);
	return (
		<DeferSiteNamingHandoffContext.Provider value={value}>
			{children}
		</DeferSiteNamingHandoffContext.Provider>
	);
};
